import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, Plus, AlertTriangle, CheckCircle, Database, History, FileSpreadsheet, Archive, RefreshCw, HardDrive } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use native XMLHttpRequest to bypass rrweb-recorder fetch wrapper
const nativeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    
    // Set headers
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: true, data, status: xhr.status });
        } catch (e) {
          resolve({ ok: true, data: xhr.responseText, status: xhr.status });
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    // Send request
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

const DataManagement = () => {
  const [tables, setTables] = useState([]);
  const [logs, setLogs] = useState([]);
  const [availableTables, setAvailableTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTable, setShowAddTable] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [uploadMode, setUploadMode] = useState('replace');
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newTable, setNewTable] = useState({ table_name: '', display_name: '', description: '' });
  
  // Backup/Restore state
  const [backupPreview, setBackupPreview] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState('replace');
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    // Call APIs sequentially to avoid race conditions with rrweb-recorder
    const loadData = async () => {
      await fetchTables();
      await fetchLogs();
    };
    loadData();
  }, []);

  const fetchTables = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(`${BACKEND_URL}/api/admin/data-management/tables`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTables(result.data);
    } catch (error) {
      console.error('Error fetching tables:', error);
      alert('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(`${BACKEND_URL}/api/admin/data-management/logs?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLogs(result.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchAvailableTables = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(`${BACKEND_URL}/api/admin/data-management/available-tables`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // Filter out already configured tables
      const unconfigured = result.data.available_tables.filter(t => !t.is_configured);
      setAvailableTables(unconfigured);
    } catch (error) {
      console.error('Error fetching available tables:', error);
    }
  };

  const handleExport = async (tableName, format = 'csv') => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/data-management/export/${tableName}?format=${format}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableName}_export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert(`Successfully exported ${tableName}`);
      fetchLogs();
    } catch (error) {
      console.error('Error exporting:', error);
      alert(error.message || 'Export failed');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedTable) {
      alert('Please select a file');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mode', uploadMode);

      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/data-management/import/${selectedTable.table_name}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      alert(`Successfully imported ${result.data.records_imported} records (${uploadMode} mode)`);
      setShowUploadDialog(false);
      setSelectedFile(null);
      setSelectedTable(null);
      fetchTables();
      fetchLogs();
    } catch (error) {
      console.error('Error importing:', error);
      alert(error.message || 'Import failed');
    }
  };

  const handleClear = async (tableName) => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/data-management/clear/${tableName}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      alert(result.data.message);
      setShowConfirmDialog(false);
      setConfirmAction(null);
      fetchTables();
      fetchLogs();
    } catch (error) {
      console.error('Error clearing:', error);
      alert(error.message || 'Clear failed');
    }
  };

  const handleClearAll = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/data-management/clear-all`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      alert(result.data.message);
      setShowConfirmDialog(false);
      setConfirmAction(null);
      fetchTables();
      fetchLogs();
    } catch (error) {
      console.error('Error clearing all:', error);
      alert(error.message || 'Clear all failed');
    }
  };

  const handleAddTable = async () => {
    if (!newTable.table_name || !newTable.display_name) {
      alert('Please fill in required fields');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/data-management/tables/add`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newTable)
        }
      );

      alert(result.data.message);
      setShowAddTable(false);
      setNewTable({ table_name: '', display_name: '', description: '' });
      fetchTables();
      fetchLogs();
    } catch (error) {
      console.error('Error adding table:', error);
      alert(error.message || 'Failed to add table');
    }
  };

  const handleRemoveTable = async (tableName) => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/data-management/tables/${tableName}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      alert(result.data.message);
      fetchTables();
      fetchLogs();
    } catch (error) {
      console.error('Error removing table:', error);
      alert(error.message || 'Failed to remove table');
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionColor = (action) => {
    const colors = {
      'export': 'blue',
      'import': 'green',
      'clear': 'red',
      'clear_all': 'red',
      'add_table': 'purple',
      'remove_table': 'orange',
      'full_backup': 'teal',
      'full_restore': 'indigo'
    };
    return colors[action] || 'gray';
  };

  // ============================================
  // FULL DATABASE BACKUP & RESTORE FUNCTIONS
  // ============================================

  const fetchBackupPreview = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await nativeFetch(`${BACKEND_URL}/api/admin/data-management/backup/preview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBackupPreview(result.data);
    } catch (error) {
      console.error('Error fetching backup preview:', error);
    }
  };

  const handleFullBackup = async () => {
    setIsBackingUp(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/data-management/backup/full`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Backup failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `visaasure_backup_${new Date().toISOString().slice(0,10)}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Full database backup downloaded successfully!');
      fetchLogs();
    } catch (error) {
      console.error('Error creating backup:', error);
      alert(error.message || 'Backup failed');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleFullRestore = async () => {
    if (!restoreFile) {
      alert('Please select a backup ZIP file');
      return;
    }

    if (!window.confirm(`⚠️ WARNING: This will ${restoreMode === 'replace' ? 'REPLACE ALL existing data with' : 'MERGE'} the backup data.\n\nAre you absolutely sure you want to proceed?`)) {
      return;
    }

    setIsRestoring(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', restoreFile);
      formData.append('mode', restoreMode);

      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/data-management/restore/full`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const data = result.data;
      let message = `✅ Restore completed!\n\nTotal records: ${data.total_records}\nCollections restored: ${data.restored_collections?.length || 0}`;
      
      if (data.errors && data.errors.length > 0) {
        message += `\n\n⚠️ Errors: ${data.errors.length}`;
      }

      alert(message);
      setShowRestoreDialog(false);
      setRestoreFile(null);
      fetchTables();
      fetchLogs();
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert(error.message || 'Restore failed');
    } finally {
      setIsRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="data-management-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="data-management-container">
      <div className="data-management-header">
        <div className="header-content">
          <Database size={32} />
          <div>
            <h1>Data Management</h1>
            <p>Manage, export, import, and clear user-generated data</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => {
              fetchAvailableTables();
              setShowAddTable(true);
            }}
          >
            <Plus size={18} />
            Add Custom Table
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              setConfirmAction('clear_all');
              setShowConfirmDialog(true);
            }}
          >
            <Trash2 size={18} />
            Clear All Data
          </button>
        </div>
      </div>

      {/* Full Database Backup & Restore Section */}
      <div className="data-management-section" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <HardDrive size={28} color="#38bdf8" />
            <div>
              <h2 style={{ color: 'white', margin: 0 }}>Full Database Backup & Restore</h2>
              <p style={{ color: '#94a3b8', margin: '4px 0 0 0', fontSize: '14px' }}>Download or restore complete database with all collections and relationships</p>
            </div>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Backup Card */}
          <div style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: '10px', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <Archive size={22} color="#38bdf8" />
              <h3 style={{ color: 'white', margin: 0 }}>Download Backup</h3>
            </div>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '16px' }}>
              Export entire database as a ZIP file containing all collections in JSON format. Use this for data migration or disaster recovery.
            </p>
            {backupPreview && (
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#e2e8f0', fontSize: '14px' }}>
                  <span>Collections:</span>
                  <span style={{ fontWeight: '600' }}>{backupPreview.total_collections}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#e2e8f0', fontSize: '14px', marginTop: '4px' }}>
                  <span>Total Records:</span>
                  <span style={{ fontWeight: '600' }}>{backupPreview.total_records?.toLocaleString()}</span>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={fetchBackupPreview}
                style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                <RefreshCw size={16} /> Preview
              </button>
              <button
                onClick={handleFullBackup}
                disabled={isBackingUp}
                style={{ flex: 2, padding: '10px', background: '#0ea5e9', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: isBackingUp ? 0.7 : 1 }}
                data-testid="download-backup-btn"
              >
                <Download size={16} /> {isBackingUp ? 'Creating Backup...' : 'Download Full Backup'}
              </button>
            </div>
          </div>

          {/* Restore Card */}
          <div style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: '10px', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <Upload size={22} color="#a855f7" />
              <h3 style={{ color: 'white', margin: 0 }}>Restore from Backup</h3>
            </div>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '16px' }}>
              Upload a backup ZIP file to restore database. Choose <strong>Replace</strong> to overwrite all data, or <strong>Merge</strong> to add/update records.
            </p>
            <div style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', padding: '10px', marginBottom: '16px' }}>
              <p style={{ color: '#fca5a5', fontSize: '12px', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={14} /> <strong>Warning:</strong> Replace mode will delete existing data. Always backup first!
              </p>
            </div>
            <button
              onClick={() => setShowRestoreDialog(true)}
              style={{ width: '100%', padding: '10px', background: '#a855f7', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              data-testid="restore-backup-btn"
            >
              <Upload size={16} /> Restore from Backup ZIP
            </button>
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <div className="data-management-section">
        <h2>Clearable Tables</h2>
        <div className="tables-grid">
          {tables.map((table) => (
            <div key={table.id} className="table-card">
              <div className="table-card-header">
                <div className="table-info">
                  <h3>{table.display_name}</h3>
                  {table.is_default && <span className="badge badge-default">Default</span>}
                </div>
                <div className="record-count">
                  <span className="count">{table.record_count}</span>
                  <span className="label">records</span>
                </div>
              </div>
              
              {table.description && (
                <p className="table-description">{table.description}</p>
              )}
              
              <div className="table-actions">
                <button
                  className="btn-icon btn-icon-blue"
                  onClick={() => handleExport(table.table_name, 'csv')}
                  title="Export as CSV"
                  disabled={table.record_count === 0}
                >
                  <Download size={16} />
                  CSV
                </button>
                <button
                  className="btn-icon btn-icon-green"
                  onClick={() => handleExport(table.table_name, 'excel')}
                  title="Export as Excel"
                  disabled={table.record_count === 0}
                >
                  <FileSpreadsheet size={16} />
                  Excel
                </button>
                <button
                  className="btn-icon btn-icon-purple"
                  onClick={() => {
                    setSelectedTable(table);
                    setShowUploadDialog(true);
                  }}
                  title="Upload Data"
                >
                  <Upload size={16} />
                  Upload
                </button>
                <button
                  className="btn-icon btn-icon-red"
                  onClick={() => {
                    setConfirmAction({ type: 'clear', table: table });
                    setShowConfirmDialog(true);
                  }}
                  title="Clear Table"
                  disabled={table.record_count === 0}
                >
                  <Trash2 size={16} />
                  Clear
                </button>
                {!table.is_default && (
                  <button
                    className="btn-icon btn-icon-orange"
                    onClick={() => handleRemoveTable(table.table_name)}
                    title="Remove from List"
                  >
                    <Trash2 size={16} />
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Audit Logs Section */}
      <div className="data-management-section">
        <h2><History size={20} /> Audit Trail (Last 50 Actions)</h2>
        <div className="audit-logs">
          {logs.length === 0 ? (
            <div className="empty-state">No audit logs yet</div>
          ) : (
            <div className="logs-table-wrapper">
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Table</th>
                    <th>Records</th>
                    <th>Admin</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>{formatTimestamp(log.timestamp)}</td>
                      <td>
                        <span className={`badge badge-${getActionColor(log.action)}`}>
                          {log.action.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>{log.table_name || '-'}</td>
                      <td>{log.record_count !== null ? log.record_count : '-'}</td>
                      <td>{log.admin_user}</td>
                      <td>
                        <span className={`status-badge ${log.status}`}>
                          {log.status === 'success' ? (
                            <CheckCircle size={14} />
                          ) : (
                            <AlertTriangle size={14} />
                          )}
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Custom Table Dialog */}
      {showAddTable && (
        <div className="modal-overlay" onClick={() => setShowAddTable(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Custom Table</h2>
              <button className="modal-close" onClick={() => setShowAddTable(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Select Table (MongoDB collection) *</label>
                <select
                  value={newTable.table_name}
                  onChange={(e) => {
                    const selectedTableName = e.target.value;
                    setNewTable({
                      table_name: selectedTableName,
                      display_name: selectedTableName.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' '),
                      description: ''
                    });
                  }}
                  className="form-select"
                >
                  <option value="">-- Select a table --</option>
                  {availableTables.map((table) => (
                    <option key={table.table_name} value={table.table_name}>
                      {table.table_name}
                    </option>
                  ))}
                </select>
                {availableTables.length === 0 && (
                  <small className="text-muted">All available tables are already configured</small>
                )}
              </div>
              <div className="form-group">
                <label>Display Name *</label>
                <input
                  type="text"
                  value={newTable.display_name}
                  onChange={(e) => setNewTable({ ...newTable, display_name: e.target.value })}
                  placeholder="e.g., Custom Data"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newTable.description}
                  onChange={(e) => setNewTable({ ...newTable, description: e.target.value })}
                  placeholder="Brief description of the data"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddTable(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddTable}>
                Add Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      {showUploadDialog && (
        <div className="modal-overlay" onClick={() => setShowUploadDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Data to {selectedTable?.display_name}</h2>
              <button className="modal-close" onClick={() => setShowUploadDialog(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Upload Mode *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="replace"
                      checked={uploadMode === 'replace'}
                      onChange={(e) => setUploadMode(e.target.value)}
                    />
                    <span>Replace All Data</span>
                    <small>Delete existing records and import new data</small>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="append"
                      checked={uploadMode === 'append'}
                      onChange={(e) => setUploadMode(e.target.value)}
                    />
                    <span>Append/Merge Data</span>
                    <small>Keep existing records and add new data</small>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>Select File (CSV or Excel) *</label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
                {selectedFile && (
                  <div className="file-info">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </div>
                )}
              </div>
              <div className="warning-box">
                <AlertTriangle size={18} />
                <div>
                  <strong>Warning:</strong> {uploadMode === 'replace' 
                    ? `This will delete all ${selectedTable?.record_count} existing records and replace with uploaded data.`
                    : 'This will add uploaded records to existing data. Duplicate IDs may cause errors.'}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={!selectedFile}
              >
                Upload Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <AlertTriangle size={32} color="#ff4444" />
              <h2>Confirm Action</h2>
            </div>
            <div className="modal-body">
              <p>
                {confirmAction === 'clear_all' 
                  ? 'Are you sure you want to clear ALL user data from ALL tables? This action cannot be undone!'
                  : `Are you sure you want to clear all data from "${confirmAction?.table?.display_name}"? This will delete ${confirmAction?.table?.record_count} records permanently.`
                }
              </p>
              <p className="warning-text">
                <strong>⚠️ This action is IRREVERSIBLE!</strong> Consider exporting data before clearing.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (confirmAction === 'clear_all') {
                    handleClearAll();
                  } else {
                    handleClear(confirmAction.table.table_name);
                  }
                }}
              >
                Yes, Clear Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Dialog */}
      {showRestoreDialog && (
        <div className="modal-overlay" onClick={() => !isRestoring && setShowRestoreDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' }}>
              <h2 style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Upload size={22} /> Restore from Backup
              </h2>
              <button className="modal-close" onClick={() => !isRestoring && setShowRestoreDialog(false)} style={{ color: 'white' }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Select Backup ZIP File</label>
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => setRestoreFile(e.target.files[0])}
                  style={{ width: '100%', padding: '10px', border: '2px dashed #d1d5db', borderRadius: '8px', cursor: 'pointer' }}
                  data-testid="restore-file-input"
                />
                {restoreFile && (
                  <p style={{ marginTop: '8px', color: '#059669', fontSize: '14px' }}>
                    ✅ Selected: {restoreFile.name} ({(restoreFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Restore Mode</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', border: restoreMode === 'replace' ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', background: restoreMode === 'replace' ? '#fef2f2' : 'white' }}>
                    <input
                      type="radio"
                      name="restoreMode"
                      value="replace"
                      checked={restoreMode === 'replace'}
                      onChange={(e) => setRestoreMode(e.target.value)}
                    />
                    <div>
                      <strong style={{ color: '#dc2626' }}>Replace</strong>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Clear all & restore</p>
                    </div>
                  </label>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', border: restoreMode === 'merge' ? '2px solid #3b82f6' : '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', background: restoreMode === 'merge' ? '#eff6ff' : 'white' }}>
                    <input
                      type="radio"
                      name="restoreMode"
                      value="merge"
                      checked={restoreMode === 'merge'}
                      onChange={(e) => setRestoreMode(e.target.value)}
                    />
                    <div>
                      <strong style={{ color: '#2563eb' }}>Merge</strong>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Add/update records</p>
                    </div>
                  </label>
                </div>
              </div>

              {restoreMode === 'replace' && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
                  <p style={{ color: '#991b1b', fontSize: '14px', margin: 0 }}>
                    <strong>⚠️ Warning:</strong> Replace mode will permanently delete ALL existing data before restoring. This action cannot be undone. Make sure you have a current backup!
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowRestoreDialog(false)}
                disabled={isRestoring}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: '#7c3aed', color: 'white' }}
                onClick={handleFullRestore}
                disabled={!restoreFile || isRestoring}
                data-testid="confirm-restore-btn"
              >
                {isRestoring ? 'Restoring...' : '🔄 Start Restore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagement;
